import type { ReactNode } from 'react';
import { AnimatePresence, LayoutGroup, motion, useReducedMotion, type Variants } from 'framer-motion';
import { ProductCard } from '../shared/ProductCard';
import type { Product } from '../../core/types/catalog';
import { ease } from '../../core/format/motion';
import { PAGE_SIZE } from './shopRules';
type Props = {
  products: Product[];
  /** Extra tegel (bijv. het huidadvies) op een vaste plek in de grid. */
  insert?: {
    index: number;
    node: ReactNode;
  };
};

/** Per kaart 40 ms na elkaar, maximaal 8 stappen: de laatste kaart voelt nooit laat. */
const STAGGER = 0.04;
const MAX_STEPS = 7;
const item: Variants = {
  hidden: {
    opacity: 0,
    y: 12
  },
  show: (step: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.26,
      ease,
      delay: Math.min(step, MAX_STEPS) * STAGGER
    }
  }),
  exit: {
    opacity: 0,
    scale: 0.98,
    transition: {
      duration: 0.15,
      ease
    }
  }
};

/**
 * De productgrid van de winkel.
 * - Eerste keer laden: kaarten komen kort na elkaar binnen (stagger).
 * - Filteren: blijvende kaarten schuiven naar hun nieuwe plek (layout), weggefilterde
 *   vervagen kort, nieuwe komen binnen. Niets springt.
 * - "Meer tonen": alleen de nieuwe kaarten komen binnen, per blok van PAGE_SIZE opnieuw gestaffeld.
 * - Minder beweging ingesteld: gewoon direct tonen.
 */
export function ProductGrid({
  products,
  insert
}: Props) {
  const reduced = useReducedMotion();
  // Tablet (zonder filterkolom) drie naast elkaar; vanaf lg staat de filterkolom ernaast, dan
  // twee, en op een breed scherm weer drie. Zo is er op geen breedte een lege, uitgerekte kaart.
  const className = 'mt-8 grid grid-cols-2 gap-x-4 gap-y-12 sm:gap-x-6 md:grid-cols-3 lg:grid-cols-2 xl:grid-cols-3';
  const cells: {
    key: string;
    node: ReactNode;
  }[] = [];
  products.forEach((p, i) => {
    if (insert && i === insert.index) cells.push({
      key: '__insert',
      node: insert.node
    });
    // De collectie heeft geen sectiekop tussen de h1 en de kaarten: de productnaam is dus een h2.
    cells.push({
      key: p.id,
      node: <ProductCard product={p} headingLevel={2} data-id="element-7034" data-mp="70n4ojk9t8ed" />
    });
  });
  if (reduced) {
    return <ul className={className} data-id="element-7035" data-mp="byf6c5apw8vr">
        {cells.map(c => <li key={c.key} className="h-full" data-id="element-7036" data-mp="bwkjglfvy6p1">
            {c.node}
          </li>)}
      </ul>;
  }
  return <LayoutGroup data-id="element-7037" data-mp="kr0bgilfeh24">
      <ul className={className} data-id="element-7038" data-mp="kridjvurrphy">
        <AnimatePresence initial={true} mode="popLayout" data-id="element-7039" data-mp="kstl86rc5hec">
          {cells.map((c, i) => <motion.li key={c.key} layout="position" custom={i % PAGE_SIZE} variants={item} initial="hidden" animate="show" exit="exit" transition={{
          layout: {
            duration: 0.24,
            ease
          }
        }} className="h-full" data-id="element-7040" data-mp="pptne6l2e006" data-mpi={i}>
              {c.node}
            </motion.li>)}
        </AnimatePresence>
      </ul>
    </LayoutGroup>;
}